<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import FileUploaderManager from "@modules/file_uploader/FileUploaderManager/FileUploaderManager";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import FileUploader from "@components/FileUploader/FileUploader.svelte";
    import Icon from "@components/Icon.svelte";
    import FileUploaderCloseIcon from "@components/icons/FileUploaderCloseIcon.svelte";
    import PlusIcon from "@components/icons/PlusIcon.svelte";
    import Spinner from "@components/Spinner/Spinner.svelte";
    import T from "@components/T.svelte";

    export let fileUploaderManager: FileUploaderManager;
    export let acceptedFileExt: string[] = [];
    export let multiple = false;

    let clickInput: () => void;

    const previewImages$ = fileUploaderManager.files$;

    const handleRemoveFile = (name: string) =>
        fileUploaderManager.removeFile(name);

    const handleAddFile = () => {
        clickInput && clickInput();
    };

    $: showPreview = $previewImages$.length > 0;
</script>

<FileUploader
    bind:clickInput
    {acceptedFileExt}
    {multiple}
    {fileUploaderManager}
    on:change
>
    <div class="gw-media-uploader">
        {#if showPreview}
            <div class="gw-media-uploader__preview">
                <ul
                    class="gw-media-uploader__preview-list gw-list-reset-default-styles"
                >
                    {#each $previewImages$ as image (image.url)}
                        <li class="gw-media-uploader__preview-item">
                            <div
                                class="gw-media-uploader__preview-item-ratio"
                            />
                            <div
                                class="gw-media-uploader__preview-item-content"
                            >
                                <BaseButton
                                    class="gw-media-uploader__preview-remove-button"
                                    on:click={() =>
                                        handleRemoveFile(image.name)}
                                    type="button"
                                >
                                    <FileUploaderCloseIcon />
                                </BaseButton>
                                {#if image.status === STATUS.LOADING}
                                    <div
                                        class="gw-media-uploader__preview-loader-container"
                                    >
                                        <Spinner size="30px" />
                                    </div>
                                {/if}
                                <img src={image.url} alt={image.key} />
                            </div>
                        </li>
                    {/each}

                    {#if $previewImages$.length < 5}
                        <li class="gw-media-uploader__preview-item">
                            <div
                                class="gw-media-uploader__preview-item-ratio"
                            />
                            <BaseButton
                                on:click={handleAddFile}
                                class="gw-media-uploader__preview-item-content gw-media-uploader__preview-photo-input gw-button-reset-default-styles"
                                type="button"
                            >
                                <Icon width={18} height={18}>
                                    <PlusIcon />
                                </Icon>
                            </BaseButton>
                        </li>
                    {/if}
                </ul>
            </div>
        {:else}
            <div class="gw-media-uploader__empty">
                <span class="gw-media-uploader__empty-title">
                    <T
                        namespace="common.MediaUploader"
                        translationKey="drag_and_drop_your_files_here"
                    />
                </span>
                <span class="gw-media-uploader__empty-permitted">
                    <T
                        namespace="common.MediaUploader"
                        translationKey="permitted"
                    />: {acceptedFileExt.join(", ") || "*"}
                </span>
                <BaseButton
                    class="gw-media-uploader__empty-add-file-btn"
                    aria-label="file upload button"
                    on:click={handleAddFile}
                    type="button"
                >
                    <span class="gw-media-uploader__empty-label-text">
                        <T
                            namespace="common.MediaUploader"
                            translationKey="add_images"
                        />
                    </span>
                </BaseButton>
            </div>
        {/if}
    </div>
</FileUploader>

<style lang="postcss">
    .gw-media-uploader {
        height: 100%;

        &__preview {
            height: 100%;
            display: flex;
            align-items: center;
            padding: var(--gw-media-uploader-preview-padding, 0.5em);

            &-list {
                width: 100%;
                flex-wrap: wrap;
                display: flex;
                gap: var(--gw-media-uploader-preview-list-gap, 0.75em);
                height: calc(100% - 10px);
            }

            &-item {
                position: relative;
                width: var(--gw-media-uploader-preview-item-width, 7.5em);

                &-ratio {
                    padding-top: 116.66%;
                    height: 0;
                }

                :global(&-content) {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    border: 1px solid
                        var(--gw-media-uploader-border-color, #667085);
                    border-radius: 10px;
                    overflow: hidden;
                }

                img {
                    height: 100%;
                    width: 100%;
                    object-fit: cover;
                }
            }

            :global(&-remove-button) {
                position: absolute;
                display: flex;
                align-items: center;
                justify-content: center;
                top: 6px;
                right: 6px;
                cursor: pointer;
                border-radius: 50%;
                background: #fff;
                transition: all 0.05s;

                &:hover {
                    box-shadow: 0px 0px 4px 2px rgba(255, 255, 255, 0.5);
                    transform: scale(1.3);
                }
            }

            &-loader-container {
                position: absolute;
                width: 100%;
                height: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: rgb(255 255 255 / 75%);
            }

            :global(&-photo-input) {
                display: flex;
                justify-content: center;
                align-items: center;
                cursor: pointer;
            }
        }

        &__empty {
            height: var(--gw-file-uploader-height, 10em);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            row-gap: 8px;

            &-title {
                font-size: 1em;
                line-height: 20px;
                font-weight: 500;
                color: var(--gw-file-uploader-color-black);
            }

            &-permitted {
                font-size: 0.75em;
                line-height: 16px;
                color: var(--gw-file-uploader-color-gray-text);
            }

            :global(&-add-file-btn) {
                display: inline-block;
                padding: 7px 12px;
                background: var(--gw-file-uploader-color-white);
                border: 1px solid #262842;
                border-radius: 8px;
                font-family: inherit;
                font-size: 0.875em;
                line-height: 18px;
                color: var(--gw-file-uploader-color-black);
                cursor: pointer;
            }
        }
    }
</style>
